/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../../integrations/supabase/client';
import type {
  EmailTemplate,
  TemplateVariable,
  IEmailTemplateRepository,
} from '../interfaces/IEmailTemplateRepository';

// Maps a hub_templates row → EmailTemplate shape
function mapHubTemplate(t: any): EmailTemplate {
  // variables in hub_templates is a jsonb object { key: 'string' } — extract keys as array
  let variables: string[] = [];
  if (t.variables) {
    if (Array.isArray(t.variables)) {
      variables = t.variables as string[];
    } else if (typeof t.variables === 'object') {
      variables = Object.keys(t.variables);
    }
  }

  return {
    id: String(t.id),
    name: t.name,
    subject: t.subject || '',
    body: t.html_content || t.text_content || '',
    template_type: (t.category as EmailTemplate['template_type']) || 'custom',
    variables,
    created_at: t.created_at,
    updated_at: t.updated_at,
    created_by: t.metadata?.created_by || '',
    is_active: t.status === 'active',
    usage_count: 0,
  };
}

// Resolve hub tenant_id from platform_tenant uuid
async function getHubTenantId(platformTenantId?: string): Promise<string | null> {
  if (!platformTenantId) return null;
  const { data } = await (supabase as any)
    .from('platform_tenants')
    .select('communications_hub_tenant_id')
    .eq('id', platformTenantId)
    .maybeSingle();
  return data?.communications_hub_tenant_id || null;
}

export class SupabaseEmailTemplateRepository implements IEmailTemplateRepository {
  private platformTenantId?: string;

  constructor(platformTenantId?: string) {
    this.platformTenantId = platformTenantId;
  }

  async getAll(): Promise<EmailTemplate[]> {
    const hubTenantId = await getHubTenantId(this.platformTenantId);

    let query = (supabase as any)
      .from('hub_templates')
      .select('*')
      .eq('status', 'active')
      .order('name');

    if (hubTenantId) {
      query = query.eq('tenant_id', hubTenantId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapHubTemplate);
  }

  async getById(id: string): Promise<EmailTemplate | null> {
    const { data, error } = await (supabase as any)
      .from('hub_templates')
      .select('*')
      .eq('id', parseInt(id))
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    if (!data) return null;
    return mapHubTemplate(data);
  }

  async getByType(type: EmailTemplate['template_type']): Promise<EmailTemplate[]> {
    const hubTenantId = await getHubTenantId(this.platformTenantId);

    let query = (supabase as any)
      .from('hub_templates')
      .select('*')
      .eq('category', type)
      .eq('status', 'active')
      .order('name');

    if (hubTenantId) {
      query = query.eq('tenant_id', hubTenantId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapHubTemplate);
  }

  async getAvailableVariables(): Promise<TemplateVariable[]> {
    return [
      { key: 'tenant_name', label: 'Tenant Name', description: 'The name of the tenant/company', example: 'Acme Corp' },
      { key: 'user_name', label: 'User Name', description: 'The full name of the user', example: 'John Doe' },
      { key: 'user_email', label: 'User Email', description: 'The email address of the user', example: 'john@example.com' },
      { key: 'portal_link', label: 'Portal Link', description: 'Link to the customer portal', example: 'https://portal.example.com' },
      { key: 'days_pending', label: 'Days Pending', description: 'Number of days the onboarding has been pending', example: '7' },
      { key: 'missing_roles', label: 'Missing Roles', description: 'Comma-separated list of incomplete roles', example: 'Account Owner, Finance Manager' },
      { key: 'completion_percentage', label: 'Completion Percentage', description: 'Percentage of onboarding completed', example: '75%' },
    ];
  }

  async create(data: {
    name: string;
    subject: string;
    body: string;
    template_type: EmailTemplate['template_type'];
    variables: string[];
    created_by: string;
  }): Promise<EmailTemplate> {
    const hubTenantId = await getHubTenantId(this.platformTenantId);

    const variables = data.variables.reduce((acc, v) => ({ ...acc, [v]: 'string' }), {});

    const { data: template, error } = await (supabase as any)
      .from('hub_templates')
      .insert({
        tenant_id: hubTenantId || 'default',
        name: data.name,
        subject: data.subject,
        html_content: data.body,
        category: data.template_type,
        variables,
        status: 'active',
        metadata: { created_by: data.created_by },
      })
      .select()
      .single();

    if (error) throw error;
    return mapHubTemplate(template);
  }

  async update(id: string, data: Partial<Omit<EmailTemplate, 'id' | 'created_at' | 'created_by'>>): Promise<EmailTemplate> {
    const updateData: any = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.subject !== undefined) updateData.subject = data.subject;
    if (data.body !== undefined) updateData.html_content = data.body;
    if (data.template_type !== undefined) updateData.category = data.template_type;
    if (data.variables !== undefined) {
      updateData.variables = data.variables.reduce((acc, v) => ({ ...acc, [v]: 'string' }), {});
    }
    if (data.is_active !== undefined) updateData.status = data.is_active ? 'active' : 'inactive';
    updateData.updated_at = new Date().toISOString();

    const { data: template, error } = await (supabase as any)
      .from('hub_templates')
      .update(updateData)
      .eq('id', parseInt(id))
      .select()
      .single();

    if (error) throw error;
    return mapHubTemplate(template);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('hub_templates')
      .update({ status: 'inactive' })
      .eq('id', parseInt(id));

    if (error) throw error;
  }

  async preview(
    template: { subject: string; body: string; variables: string[] },
    sampleData: Record<string, string>
  ): Promise<{ subject: string; body: string }> {
    let previewSubject = template.subject;
    let previewBody = template.body;

    template.variables.forEach((variable) => {
      const value = sampleData[variable] || `{{${variable}}}`;
      const regex = new RegExp(`{{${variable}}}`, 'g');
      previewSubject = previewSubject.replace(regex, value);
      previewBody = previewBody.replace(regex, value);
    });

    return { subject: previewSubject, body: previewBody };
  }
}
